import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Camera, Files, ClipboardText, CurrencyEur, Plus, Trash, X, ListBullets, FileText,
  MapPin, Check, PaperPlaneTilt, Spinner,
} from "@phosphor-icons/react";
import MobileShell from "@/mobile/MobileShell";
import VoiceDictation from "@/mobile/VoiceDictation";
import MobilePreliminarSheet from "@/mobile/MobilePreliminarSheet";
import GabineteChip from "@/components/GabineteChip";
import { useGabinetesMap } from "@/hooks/useGabinetesMap";

const eur = (n) => (n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const fmtDateTime = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
const newId = () => `rpg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Vista móvil simplificada del expediente para el perito en campo.
 *
 * Muestra:
 *   • Circunstancias del caso
 *   • Botón → bottom sheet "Gestiones del encargo" (timeline 01,02,03...)
 *   • Notas de campo (dictado por voz)
 *   • Reserva preventiva (lista garantía→importe)
 *   • Atajos a Fotos / Documentos
 */
export default function MobileExpediente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { byId: gabineteById } = useGabinetesMap();
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGestiones, setShowGestiones] = useState(false);
  const [showPreliminar, setShowPreliminar] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/expedientes/${id}`);
      setExp(r.data);
    } catch (e) {
      toast.error("Error cargando expediente");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const saveNotas = async (text) => {
    // Endpoint dedicado al móvil: además de persistir
    // `observaciones_especiales`, añade automáticamente una entrada en
    // "Gestiones del encargo" para que el gestor vea en el timeline qué
    // dictó el perito en la visita. Idempotente: si el texto no cambia,
    // no genera duplicados.
    const r = await api.put(`/expedientes/${id}/mobile/field-notes`, { text });
    setExp((p) => ({
      ...p,
      observaciones_especiales: text,
      // Si el backend creó una nueva gestión, recargamos para tener el listado fresco
      // (no devolvemos la gestión inline para no acoplar el contrato).
      ...(r.data?.appended_gestion ? {} : {}),
    }));
    if (r.data?.appended_gestion) reload();
  };

  const saveReservas = async (items) => {
    await api.put(`/expedientes/${id}`, { reserva_preventiva_garantias: items });
    setExp((p) => ({ ...p, reserva_preventiva_garantias: items }));
  };

  // Geolocalización + marcar visita realizada. Si el navegador deniega la
  // ubicación, sigue marcando la visita pero sin coords (con aviso al perito).
  const [markingVisit, setMarkingVisit] = useState(false);
  const markVisitDone = async () => {
    if (markingVisit) return;
    setMarkingVisit(true);
    try {
      let coords = null;
      try {
        coords = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error("Geo no soportada"));
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos.coords),
            (err) => reject(err),
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
          );
        });
      } catch (geoErr) {
        toast.info("Sin GPS: marco la visita igualmente.");
      }
      const body = coords ? {
        lat: coords.latitude,
        lon: coords.longitude,
        accuracy_m: coords.accuracy,
      } : {};
      const r = await api.post(`/expedientes/${id}/mark-visit`, body);
      toast.success(coords ? "Visita marcada con GPS" : "Visita marcada (sin GPS)");
      // Refrescar fecha_visita + gestiones
      setExp((p) => ({
        ...p,
        fecha_visita: r.data.fecha_visita,
        ubicacion_visita: r.data.ubicacion_visita,
        gestiones: [...(p.gestiones || []), r.data.gestion],
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error marcando la visita");
    } finally {
      setMarkingVisit(false);
    }
  };

  // Añadir una gestión nueva al timeline
  const addGestion = async (text) => {
    const r = await api.post(`/expedientes/${id}/gestiones`, { text });
    setExp((p) => ({ ...p, gestiones: [...(p.gestiones || []), r.data.gestion] }));
    return r.data.gestion;
  };

  if (loading || !exp) {
    return (
      <MobileShell title="Expediente" backTo="/m/expedientes">
        <div className="text-center text-muted text-xs py-10">Cargando…</div>
      </MobileShell>
    );
  }

  const ed = exp.extracted_data || {};
  const nombre = ed.asegurado_nombre || "—";
  const numSiniestro = ed.numero_siniestro || exp.id?.slice(0, 8);
  const aseguradora = ed.aseguradora || exp.detected_aseguradora;
  const gabinete = gabineteById(exp.gabinete_id);
  const circunstancias = ed.observaciones || exp.observaciones_siniestro;

  return (
    <MobileShell title="Expediente" backTo="/m/expedientes">
      {/* Cabecera */}
      <div className="bg-white border-b border-[#E8EAED] px-3 py-3">
        <div className="text-[18px] font-bold leading-tight">{nombre}</div>
        <div className="text-[12px] text-muted mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span>Nº {numSiniestro}</span>
          {aseguradora && (
            <>
              <span>·</span>
              <span>{aseguradora}</span>
              <GabineteChip gabinete={gabinete} size="xs" />
            </>
          )}
        </div>
      </div>

      {/* CTA Visita realizada (oculta si ya está marcada) */}
      <VisitMarkRow
        expediente={exp}
        markingVisit={markingVisit}
        onMark={markVisitDone}
      />

      {/* Circunstancias */}
      <Section
        title="Circunstancias del caso"
        subtitle="Resumen del siniestro y notas del gestor."
      >
        <p className="text-[13px] whitespace-pre-wrap leading-relaxed" data-testid="exp-circunstancias">
          {circunstancias || <span className="text-muted italic">Sin circunstancias registradas.</span>}
        </p>
      </Section>

      {/* Gestiones del encargo (botón → bottom sheet) */}
      <ButtonRow
        icon={<ClipboardText className="w-5 h-5 text-blue-600" weight="duotone" />}
        title="Gestiones del encargo"
        subtitle={exp.gestiones?.length ? `${exp.gestiones.length} gestión${exp.gestiones.length === 1 ? "" : "es"}` : "Sin gestiones"}
        onClick={() => setShowGestiones(true)}
        testid="open-gestiones"
      />

      {/* Notas de campo (dictado) */}
      <Section
        title="Notas de campo"
        subtitle="Dicta lo que veas en la inspección. Aparecerán en la pestaña Expediente de la web y se podrán enriquecer con IA para el preliminar."
      >
        <VoiceDictation
          value={exp.observaciones_especiales || ""}
          onSave={saveNotas}
          placeholder="Ej. Daños por agua procedentes del 3ºB. Mancha extensa en techo del salón…"
        />
      </Section>

      {/* Reserva preventiva */}
      <Section title="Reserva preventiva" subtitle="Importe por garantía para el preliminar.">
        <ReservaPreventivaEditor
          items={exp.reserva_preventiva_garantias || []}
          onSave={saveReservas}
        />
      </Section>

      {/* Atajos Fotos + Docs */}
      <div className="px-3 mt-3 grid grid-cols-2 gap-2">
        <ShortcutCard
          onClick={() => navigate(`/m/expediente/${id}/fotos`)}
          icon={<Camera className="w-6 h-6 text-pink-600" weight="duotone" />}
          label="Fotos"
          testid="open-fotos"
        />
        <ShortcutCard
          onClick={() => navigate(`/m/expediente/${id}/docs`)}
          icon={<Files className="w-6 h-6 text-blue-600" weight="duotone" />}
          label="Documentos"
          testid="open-docs"
        />
      </div>

      {/* Emitir informe preliminar (acción destacada) */}
      <div className="px-3 mt-3">
        <button
          type="button"
          onClick={() => setShowPreliminar(true)}
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white text-[14px] font-semibold py-3 rounded-md shadow-sm hover:bg-blue-700 active:translate-y-px"
          data-testid="emit-preliminar"
        >
          <FileText className="w-5 h-5" weight="fill" />
          Emitir informe preliminar
        </button>
        <p className="text-[11px] text-muted text-center mt-1.5 leading-tight">
          Desarrolla las notas con IA y emite el PDF incluyendo fotos, reserva y documentos.
        </p>
      </div>

      <div className="h-6" />

      {/* Bottom sheet: Gestiones del encargo */}
      {showGestiones && (
        <GestionesSheet
          gestiones={exp.gestiones || []}
          observacionesEncargo={exp.observaciones_encargo || exp.anotacion_encargo}
          onClose={() => setShowGestiones(false)}
          onAdd={addGestion}
        />
      )}

      {/* Bottom sheet: Emitir preliminar */}
      {showPreliminar && (
        <MobilePreliminarSheet
          expediente={exp}
          onClose={() => setShowPreliminar(false)}
          onGenerated={() => reload()}
        />
      )}
    </MobileShell>
  );
}

function VisitMarkRow({ expediente, markingVisit, onMark }) {
  const visited = !!expediente.fecha_visita;
  const ubi = expediente.ubicacion_visita;
  if (visited) {
    return (
      <div className="mx-3 mt-3 bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2"
           data-testid="visit-marked-banner">
        <div className="w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
          <Check className="w-4 h-4 text-white" weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-emerald-900">
            Visita realizada el {new Date(expediente.fecha_visita).toLocaleDateString("es-ES")}
          </div>
          {ubi?.lat && (
            <a href={`https://www.google.com/maps?q=${ubi.lat},${ubi.lon}`}
               target="_blank" rel="noreferrer"
               className="text-[11px] text-emerald-800 underline flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" weight="fill" />
              {ubi.lat.toFixed(5)}, {ubi.lon.toFixed(5)}
              {ubi.accuracy_m && ` · ±${Math.round(ubi.accuracy_m)} m`}
            </a>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="mx-3 mt-3">
      <button
        onClick={onMark}
        disabled={markingVisit}
        className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-white text-[14px] font-semibold py-3 rounded-md shadow-sm hover:bg-zinc-900 active:translate-y-px disabled:opacity-70"
        data-testid="mark-visit-btn"
      >
        {markingVisit
          ? <><Spinner className="w-5 h-5 animate-spin" /> Obteniendo ubicación…</>
          : <><MapPin className="w-5 h-5" weight="fill" /> Marcar visita realizada (con GPS)</>}
      </button>
      <p className="text-[11px] text-muted text-center mt-1.5 leading-tight">
        Captura tus coordenadas y las añade a "Gestiones del encargo" para que el gestor pueda localizar el lugar en el catastro.
      </p>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="mt-3 mx-3 bg-white rounded-lg border border-[#E8EAED] p-3">
      <div className="text-[11px] uppercase tracking-wide font-semibold text-muted">{title}</div>
      {subtitle && <div className="text-[11px] text-muted mt-0.5">{subtitle}</div>}
      <div className="mt-2">{children}</div>
    </section>
  );
}

function ButtonRow({ icon, title, subtitle, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-3 mx-3 w-[calc(100%-1.5rem)] bg-white rounded-lg border border-[#E8EAED] px-3 py-3 flex items-center gap-3 hover:bg-zinc-50 active:translate-y-px"
      data-testid={testid}
    >
      <div className="w-9 h-9 bg-zinc-50 rounded-md flex items-center justify-center">{icon}</div>
      <div className="flex-1 text-left">
        <div className="font-semibold text-[14px]">{title}</div>
        <div className="text-[11px] text-muted">{subtitle}</div>
      </div>
      <ListBullets className="w-4 h-4 text-muted" weight="bold" />
    </button>
  );
}

function ShortcutCard({ onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-white rounded-lg border border-[#E8EAED] py-5 flex flex-col items-center justify-center gap-1 hover:bg-zinc-50 active:translate-y-px"
      data-testid={testid}
    >
      {icon}
      <span className="font-semibold text-[13px]">{label}</span>
    </button>
  );
}

function ReservaPreventivaEditor({ items: initialItems, onSave }) {
  const [items, setItems] = useState(initialItems);
  const [saving, setSaving] = useState(false);
  const total = useMemo(() => items.reduce((a, i) => a + (Number(i.importe) || 0), 0), [items]);

  const updateField = (idx, k, v) => {
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  };
  const add = () => setItems((p) => [...p, { id: newId(), garantia: "", importe: 0 }]);
  const remove = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  const persist = async () => {
    setSaving(true);
    try {
      // Normaliza importes a number
      const clean = items
        .filter((it) => (it.garantia || "").trim() || Number(it.importe) > 0)
        .map((it) => ({ ...it, importe: Number(it.importe) || 0 }));
      await onSave(clean);
      setItems(clean);
      toast.success("Reserva guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <div className="text-[12px] text-muted italic">Sin partidas. Añade la primera.</div>
      )}
      {items.map((it, idx) => (
        <div key={it.id || idx} className="flex items-center gap-2" data-testid={`reserva-row-${idx}`}>
          <input
            type="text"
            value={it.garantia || ""}
            onChange={(e) => updateField(idx, "garantia", e.target.value)}
            placeholder="Garantía (Continente, Contenido, RC…)"
            className="flex-1 text-[13px] py-2 px-2 border border-[#E8EAED] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
            data-testid={`reserva-garantia-${idx}`}
          />
          <div className="relative">
            <input
              type="number"
              inputMode="decimal"
              value={it.importe ?? 0}
              onChange={(e) => updateField(idx, "importe", e.target.value)}
              className="w-24 text-[13px] py-2 pl-2 pr-6 text-right border border-[#E8EAED] rounded-md tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-200"
              data-testid={`reserva-importe-${idx}`}
            />
            <CurrencyEur className="w-3.5 h-3.5 text-muted absolute right-1.5 top-1/2 -translate-y-1/2" weight="bold" />
          </div>
          <button
            onClick={() => remove(idx)}
            className="p-2 text-muted hover:text-red-600"
            aria-label="Eliminar"
            data-testid={`reserva-del-${idx}`}
          >
            <Trash className="w-4 h-4" weight="bold" />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={add}
          className="inline-flex items-center gap-1 text-[12px] font-semibold py-2 px-3 rounded-md bg-white border border-[#E8EAED] hover:bg-zinc-50"
          data-testid="reserva-add"
        >
          <Plus className="w-3.5 h-3.5" weight="bold" /> Añadir garantía
        </button>
        <div className="flex-1 text-right text-[13px] font-semibold tabular-nums" data-testid="reserva-total">
          Total: {eur(total)}
        </div>
      </div>
      <button
        onClick={persist}
        disabled={saving}
        className="w-full mt-1 inline-flex items-center justify-center gap-1.5 bg-emerald-600 text-white text-[13px] font-semibold py-2.5 rounded-md disabled:opacity-50 hover:bg-emerald-700"
        data-testid="reserva-save"
      >
        Guardar reserva
      </button>
    </div>
  );
}

function GestionesSheet({ gestiones, observacionesEncargo, onClose, onAdd }) {
  // Ordena por fecha descendente
  const sorted = [...gestiones].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const t = text.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      await onAdd?.(t);
      setText("");
      toast.success("Nota añadida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando la nota");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-end" data-testid="gestiones-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-xl max-h-[88vh] flex flex-col">
        <div className="flex items-center gap-2 px-3 py-3 border-b border-[#E8EAED]">
          <h3 className="font-semibold text-[15px] flex-1">Gestiones del encargo</h3>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-zinc-100" aria-label="Cerrar" data-testid="gestiones-close">
            <X className="w-5 h-5" weight="bold" />
          </button>
        </div>

        {/* Composer — añadir nueva gestión */}
        {onAdd && (
          <div className="border-b border-[#E8EAED] bg-zinc-50 px-3 py-2.5"
               data-testid="gestion-composer">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Escribe una nota para añadir al timeline…"
              rows={2}
              className="w-full text-[13px] resize-none border border-[#E8EAED] rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              data-testid="gestion-input"
            />
            <div className="flex justify-between items-center mt-1.5">
              <span className="text-[10.5px] text-muted">
                {text.length > 0 ? `${text.length} caracteres` : "Visible para todo el equipo."}
              </span>
              <button
                onClick={submit}
                disabled={!text.trim() || saving}
                data-testid="gestion-submit"
                className="inline-flex items-center gap-1.5 bg-blue-600 text-white text-[12px] font-semibold px-3 py-1.5 rounded-md disabled:opacity-40 hover:bg-blue-700">
                {saving
                  ? <><Spinner className="w-3 h-3 animate-spin" /> Guardando…</>
                  : <><PaperPlaneTilt className="w-3 h-3" weight="fill" /> Añadir nota</>}
              </button>
            </div>
          </div>
        )}

        <div className="overflow-y-auto p-3 space-y-2.5">
          {observacionesEncargo && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">Observaciones del encargo</div>
              <p className="text-[13px] whitespace-pre-wrap text-amber-900 mt-1" data-testid="gestiones-observaciones">{observacionesEncargo}</p>
            </div>
          )}
          {sorted.length === 0 && !observacionesEncargo && (
            <div className="text-center text-muted text-[12px] py-6" data-testid="gestiones-empty">Sin gestiones registradas.</div>
          )}
          {sorted.map((g, idx) => (
            <div key={g.id || idx} className="bg-white border border-[#E8EAED] rounded-md p-2.5" data-testid={`gestion-${idx}`}>
              <div className="flex items-center justify-between text-[10px] text-muted">
                <span className="font-semibold">
                  {String(sorted.length - idx).padStart(2, "0")} · {g.created_by_name || g.created_by_code || "—"}
                </span>
                <span>{g.created_at ? fmtDateTime(g.created_at) : ""}</span>
              </div>
              <p className="text-[13px] whitespace-pre-wrap mt-1">{g.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
